import { create } from 'zustand';
import { criarCliente, ErroApi, type Cliente } from './api';

/**
 * A sessão da PLATAFORMA — separada da do escritório, de ponta a ponta.
 *
 * ## Por que outro store, e outro cliente de API
 * Não é organização. Se os dois tokens compartilhassem o mesmo lugar, uma tela
 * de escritório poderia mandar o token de operador (ou o contrário) sem que
 * nada no código dissesse que isso aconteceu — e o servidor recusaria com 401
 * num ponto qualquer, longe da causa.
 *
 * Com dois caminhos separados, mandar o token errado exige importar o cliente
 * errado, que é visível na linha do import.
 *
 * ## Por que `sessionStorage`, e não `localStorage`
 * O token de operador atravessa todos os escritórios. `localStorage` sobrevive
 * ao fechamento do navegador; `sessionStorage` morre com a aba. Para uma
 * credencial desse peso, a sessão terminar quando a pessoa fecha a janela é o
 * comportamento certo — e como não há refresh, ela terminaria em 30 min de
 * qualquer forma.
 */

const CHAVE = 'social.operador.token';

export interface Operador {
  id: string;
  nome: string;
  email: string;
  nivel: 'SUPORTE' | 'ADMIN';
}

interface EstadoOperador {
  token: string | null;
  operador: Operador | null;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => void;
}

/** Lê o que sobreviveu ao reload. Um `try` porque o acessador pode lançar. */
function guardado(): { token: string | null; operador: Operador | null } {
  try {
    const bruto = sessionStorage.getItem(CHAVE);
    if (!bruto) return { token: null, operador: null };
    return JSON.parse(bruto) as { token: string; operador: Operador };
  } catch {
    // Navegador com armazenamento bloqueado, aba anônima, JSON corrompido: a
    // pessoa entra de novo, que é melhor que a tela não abrir.
    return { token: null, operador: null };
  }
}

export const useOperador = create<EstadoOperador>((set) => ({
  ...guardado(),

  entrar: async (email, senha) => {
    const resposta = await fetch('/api/operador/entrar', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, senha }),
    });
    if (!resposta.ok) {
      const corpo = (await resposta.json().catch(() => ({}))) as { mensagem?: string };
      throw new ErroApi(
        'credenciais_invalidas',
        corpo.mensagem ?? 'E-mail ou senha incorretos.',
        resposta.status,
      );
    }

    const s = (await resposta.json()) as { token: string; operador: Operador };
    try {
      sessionStorage.setItem(CHAVE, JSON.stringify(s));
    } catch {
      // Sem armazenamento, a sessão vive só em memória: recarregar a página
      // desloga. É pior que o normal, e melhor que não deixar entrar.
    }
    set({ token: s.token, operador: s.operador });
  },

  sair: () => {
    try {
      sessionStorage.removeItem(CHAVE);
    } catch {
      // Nada a fazer: o estado em memória some abaixo de qualquer forma.
    }
    set({ token: null, operador: null });
  },
}));

/**
 * O cliente de API da plataforma.
 *
 * `aoRenovar` LANÇA de propósito: não há refresh de operador. Quando o token
 * de 30 minutos expira, a pessoa entra de novo — e o erro aqui é o que faz a
 * tela mandá-la para o login em vez de girar numa renovação que não existe.
 */
export const apiOperador: Cliente = criarCliente({
  buscar: (...args) => fetch(...args),
  obterAcesso: () => useOperador.getState().token,
  aoRenovar: () => {
    useOperador.getState().sair();
    return Promise.reject(
      new ErroApi('sessao_expirada', 'Sessão expirada. Entre de novo.', 401),
    );
  },
});
